import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, Truck, AlertCircle, X, Loader2, CheckCircle2, Send, Download, ShieldCheck,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navigation from '../components/Navigation';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';

const ShopMyOrders = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [orders, setOrders] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('orders');   // orders | claims
  const [claiming, setClaiming] = useState(null); // order

  const load = async () => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([
        axios.get('/api/shop/orders/mine'),
        axios.get('/api/shop/claims/mine'),
      ]);
      setOrders(o.data?.orders || []);
      setClaims(c.data?.claims || []);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const fmt = (cents, cur) => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: (cur || 'eur').toUpperCase() }).format((cents || 0) / 100); }
    catch { return `${((cents || 0) / 100).toFixed(2)} ${cur || 'EUR'}`; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-950 via-black to-violet-950 text-white pb-24" data-testid="shop-my-orders">
      <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="myorders-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-bold flex items-center gap-2"><Package className="w-5 h-5 text-fuchsia-400" /> {t('eshop.my_orders', 'Mes commandes')}</h1>
      </header>
      <nav className="flex border-b border-white/10 px-2">
        <button onClick={() => setTab('orders')} className={`px-4 py-3 text-sm font-semibold border-b-2 ${tab === 'orders' ? 'text-fuchsia-300 border-fuchsia-500' : 'text-gray-400 border-transparent'}`} data-testid="myorders-tab-orders">
          {t('eshop.orders', 'Commandes')} ({orders.length})
        </button>
        <button onClick={() => setTab('claims')} className={`px-4 py-3 text-sm font-semibold border-b-2 ${tab === 'claims' ? 'text-red-300 border-red-500' : 'text-gray-400 border-transparent'}`} data-testid="myorders-tab-claims">
          {t('eshop.claims', 'Réclamations')} ({claims.length})
        </button>
      </nav>
      <main className="px-4 py-4 space-y-2">
        {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-8" /> :
         tab === 'orders' ? (
          orders.length === 0 ? <p className="text-center text-sm text-gray-400 py-8">{t('eshop.no_orders', 'Aucune commande')}</p> :
          orders.map(o => (
            <div key={o.id} className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`my-order-${o.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{o.product_title}</p>
                  <p className="text-xs text-gray-400">{fmt(o.amount_cents, o.currency)} · {o.quantity}×</p>
                  <p className="text-[11px] text-gray-500">{new Date(o.created_at).toLocaleString('fr-FR')}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  o.status === 'shipped' ? 'bg-emerald-500/20 text-emerald-300' :
                  o.status === 'paid'    ? 'bg-yellow-500/20 text-yellow-300' :
                                           'bg-gray-500/20 text-gray-300'
                }`}>{o.status}</span>
              </div>
              {o.tracking_number && (
                <p className="text-xs text-emerald-300 mt-2 flex items-center gap-1.5">
                  <Truck className="w-3 h-3" /> {t('eshop.tracking_number', 'N° de suivi')}: <span className="font-mono">{o.tracking_number}</span>
                </p>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(o.status === 'paid' || o.status === 'shipped' || o.status === 'delivered') && (
                  <a
                    href={`${(process.env.REACT_APP_BACKEND_URL || '')}/api/shop/orders/${o.id}/invoice.pdf?lang=${language || 'fr'}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1 rounded-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-xs font-semibold flex items-center gap-1"
                    data-testid={`download-invoice-${o.id}`}
                  >
                    <Download className="w-3 h-3" /> {t('eshop.download_invoice', 'Télécharger la facture')}
                  </a>
                )}
                {(o.status === 'paid' || o.status === 'shipped') && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(t('eshop.confirm_delivery_msg', 'Confirmez-vous que le service / produit a bien été honoré ?'))) return;
                      try {
                        await axios.post(`/api/shop/orders/${o.id}/confirm-delivery`);
                        toast.success(t('eshop.delivery_confirmed', 'Merci ! Service validé.'));
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                    }}
                    className="px-3 py-1 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-xs font-semibold flex items-center gap-1"
                    data-testid={`confirm-delivery-${o.id}`}
                  >
                    <ShieldCheck className="w-3 h-3" /> {t('eshop.validate_service', 'Valider la réalisation')}
                  </button>
                )}
                {(o.status === 'paid' || o.status === 'shipped') && (
                  <button
                    onClick={() => setClaiming(o)}
                    className="px-3 py-1 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs font-semibold flex items-center gap-1"
                    data-testid={`open-claim-${o.id}`}
                  >
                    <AlertCircle className="w-3 h-3" /> {t('eshop.file_claim', 'Ouvrir une réclamation')}
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          claims.length === 0 ? <p className="text-center text-sm text-gray-400 py-8">{t('eshop.no_claims', 'Aucune réclamation')}</p> :
          claims.map(c => (
            <div key={c.id} className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`my-claim-${c.id}`}>
              <p className="font-semibold text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-400" /> {c.reason}</p>
              <p className="text-xs text-gray-300 mt-1">{c.message}</p>
              <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] ${c.status === 'open' ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'}`}>{c.status}</span>
              {c.seller_response && <p className="text-xs text-emerald-300 mt-2 italic">{t('eshop.seller_response', 'Réponse du vendeur')} : « {c.seller_response} »</p>}
            </div>
          ))
        )}
      </main>

      {claiming && (
        <ClaimModal
          order={claiming}
          onClose={() => setClaiming(null)}
          onSent={() => { setClaiming(null); load(); }}
        />
      )}
      <Navigation />
    </div>
  );
};

const ClaimModal = ({ order, onClose, onSent }) => {
  const { t } = useLanguage();
  const kbHeight = useKeyboardHeight();
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const REASONS = [
    t('eshop.claim_not_received', 'Colis non reçu'),
    t('eshop.claim_damaged', 'Produit endommagé'),
    t('eshop.claim_wrong_item', 'Mauvais article'),
    t('eshop.claim_quality', 'Qualité non conforme'),
    t('eshop.claim_other', 'Autre'),
  ];

  const submit = async () => {
    if (!reason) return toast.error(t('eshop.pick_reason', 'Sélectionnez un motif'));
    if (!message.trim()) return toast.error(t('eshop.claim_message_required', 'Décrivez votre réclamation'));
    setBusy(true);
    try {
      await axios.post('/api/shop/claims', { order_id: order.id, reason, message });
      toast.success(t('eshop.claim_sent', 'Réclamation envoyée'));
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-6"
      onClick={onClose}
      style={{ paddingBottom: kbHeight > 0 ? kbHeight + 16 : undefined }}
    >
      <div className="w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-3xl flex flex-col"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight + 32}px)` : '85vh' }}
        data-testid="claim-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="font-semibold flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-400" /> {t('eshop.file_claim', 'Ouvrir une réclamation')}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <p className="text-xs text-gray-400">{t('eshop.claim_for', 'Pour la commande')} : <span className="text-white font-semibold">{order.product_title}</span></p>
          <div className="space-y-1.5">
            {REASONS.map(r => (
              <label key={r} className="flex items-center gap-2 p-2.5 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10">
                <input type="radio" checked={reason === r} onChange={() => setReason(r)} className="accent-red-500" />
                <span className="text-sm">{r}</span>
              </label>
            ))}
          </div>
          <textarea
            value={message} onChange={e => setMessage(e.target.value)} rows={4}
            placeholder={t('eshop.claim_details', 'Décrivez votre réclamation…')}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-500"
            data-testid="claim-message"
          />
          <button onClick={submit} disabled={busy} className="w-full py-2.5 rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2" data-testid="claim-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> {t('eshop.send_claim', 'Envoyer la réclamation')}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopMyOrders;
